/**
 * identity-cookies.js — mirrors the client-canonical `localStorage.lang` into a same-named cookie,
 * so a server-rendered GET can read the requester's language override without inventing a profile
 * store (game-history-htmx-spike design § Identity & language: the cookie seam). A plain browser
 * navigation — an `<a>` click, a reload — carries cookies but no request body or custom header we
 * control; cookies are the one seam a server-rendered GET can read. Consumer: the stock
 * `CookieLocaleResolver("lang")` (`LocaleConfig`). The module used to also mirror
 * `localStorage.browserId` for `DeviceTokenFilter`'s legacy-cookie adoption seam; that half retired
 * with the seam itself (the identity-retirement tail, 2026-07-15).
 */
import { LANG_OVERRIDE_KEY } from "./i18n.js";

const MIRRORED_KEYS = [LANG_OVERRIDE_KEY];
const COOKIE_MAX_AGE_SECONDS = 31536000; // 1 year — matches localStorage's effective permanence.

/**
 * Writes a same-named cookie for every mirrored key whose localStorage value exists and differs
 * from the current cookie, then re-reads `document.cookie` to confirm the write actually stuck
 * (a cookies-blocked browser silently drops the assignment). Returns true only when something
 * changed AND every mirrored, present localStorage value is verifiably readable back from the
 * cookie jar afterward — the two-leg check callers (the games.html self-heal reload) rely on.
 */
export function syncIdentityCookies() {
  let wrote = false;
  for (const key of MIRRORED_KEYS) {
    const value = localStorage.getItem(key);
    if (value == null || readCookie(key) === value) continue;
    document.cookie = `${key}=${encodeURIComponent(value)}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
    wrote = true;
  }
  if (!wrote) return false;
  return MIRRORED_KEYS.every((key) => {
    const value = localStorage.getItem(key);
    return value == null || readCookie(key) === value;
  });
}

function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}
